// src/services/ownerOnboardingService.ts
//
// Client for the backend Owner Onboarding API (base path /api/owner-onboarding).
// Every response is the standard { success, message, data } envelope.
//
// - PUBLIC and OWNER calls use a dedicated axios instance so they are NOT affected
//   by the admin auth interceptor (Bearer token / 401 redirect) in utils/api.ts.
//   Owner authentication is done with the `X-Owner-Session` header instead.
// - ADMIN calls use the shared `api` instance, which automatically attaches the
//   admin JWT (Authorization: Bearer <authToken> from localStorage).

import axios from 'axios';
import api from '../utils/api';
import {
    AddAmendmentRequest,
    AdminOnboarding,
    AdminProfileUpdate,
    AmendmentDocKey,
    ApiResponse,
    CreateOnboardingRequest,
    IdentityDocument,
    OnboardingOwnerData,
    OnboardingOwnerRaw,
    OnboardingPublicInfo,
    AvailabilityData,
    OwnerForm,
    PropertySetup,
    RequestOtpResult,
    RequestSignOtpResult,
    SignRequest,
    VerifyOtpResult,
} from '../types';

// Same base URL resolution as utils/api.ts (keeps env configuration consistent).
const publicApi = axios.create({
    baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8080/api',
    headers: { 'Content-Type': 'application/json' },
});

const ROOT = '/owner-onboarding';

const ownerHeaders = (sessionToken: string) => ({
    headers: { 'X-Owner-Session': sessionToken },
});

function safeParse<T>(raw: string | null | undefined): T | null {
    if (!raw) return null;
    try {
        return JSON.parse(raw) as T;
    } catch {
        return null;
    }
}

// ------------------------- PUBLIC -------------------------

export async function getPublicInfo(slug: string): Promise<OnboardingPublicInfo> {
    const res = await publicApi.get<ApiResponse<OnboardingPublicInfo>>(`${ROOT}/public/${slug}`);
    return res.data.data;
}

export async function requestOtp(slug: string): Promise<RequestOtpResult> {
    const res = await publicApi.post<ApiResponse<RequestOtpResult>>(`${ROOT}/public/${slug}/request-otp`);
    return res.data.data;
}

export async function verifyOtp(slug: string, code: string): Promise<VerifyOtpResult> {
    const res = await publicApi.post<ApiResponse<VerifyOtpResult>>(`${ROOT}/public/${slug}/verify-otp`, { code });
    return res.data.data;
}

// ------------------------- OWNER -------------------------

export async function getOwnerData(slug: string, sessionToken: string): Promise<OnboardingOwnerData> {
    const res = await publicApi.get<ApiResponse<OnboardingOwnerRaw>>(`${ROOT}/owner/${slug}`, ownerHeaders(sessionToken));
    const raw = res.data.data;
    return {
        ...raw,
        propertySetup: safeParse<PropertySetup>(raw.propertySetupData),
        ownerForm: safeParse<OwnerForm>(raw.ownerFormData),
    };
}

export async function savePropertySetup(
    slug: string,
    sessionToken: string,
    propertySetup: PropertySetup,
): Promise<void> {
    await publicApi.put<ApiResponse<unknown>>(`${ROOT}/owner/${slug}/property-setup`, propertySetup, ownerHeaders(sessionToken));
}

export async function saveOwnerForm(
    slug: string,
    sessionToken: string,
    ownerForm: OwnerForm,
): Promise<void> {
    await publicApi.put<ApiResponse<unknown>>(`${ROOT}/owner/${slug}/owner-form`, ownerForm, ownerHeaders(sessionToken));
}


export async function saveAvailability(
    slug: string,
    sessionToken: string,
    availability: AvailabilityData,
): Promise<void> {
    await publicApi.put<ApiResponse<unknown>>(`${ROOT}/owner/${slug}/availability`, availability, ownerHeaders(sessionToken));
}

/**
 * Uploads one identity/bank document (PDF or PNG/JPEG image) via multipart form.
 * The browser sets the multipart Content-Type (with boundary) automatically — we
 * explicitly clear the instance-default `application/json` so this is not clobbered.
 * Returns the updated list of uploaded documents. 400 on invalid file type.
 */
export async function uploadIdentityDocument(
    slug: string,
    sessionToken: string,
    file: File,
): Promise<IdentityDocument[]> {
    const form = new FormData();
    form.append('file', file);
    // Use raw axios (not publicApi) so the instance-default `Content-Type: application/json`
    // does NOT leak onto this multipart request. With no Content-Type set, the browser adds
    // `multipart/form-data; boundary=...` itself, which the backend needs to bind @RequestParam("file").
    const base = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';
    // Backend returns the updated list DIRECTLY as `data` (not wrapped in { documents }).
    const res = await axios.post<ApiResponse<IdentityDocument[]>>(
        `${base}${ROOT}/owner/${slug}/documents`,
        form,
        { headers: { 'X-Owner-Session': sessionToken } },
    );
    return res.data.data ?? [];
}

/** Removes one uploaded identity document by index. Returns the updated list. */
export async function deleteIdentityDocument(
    slug: string,
    sessionToken: string,
    index: number,
): Promise<IdentityDocument[]> {
    const res = await publicApi.delete<ApiResponse<IdentityDocument[]>>(
        `${ROOT}/owner/${slug}/documents/${index}`,
        ownerHeaders(sessionToken),
    );
    return res.data.data ?? [];
}

export async function signOnboarding(
    slug: string,
    sessionToken: string,
    payload: SignRequest,
): Promise<OnboardingOwnerRaw> {
    const res = await publicApi.post<ApiResponse<OnboardingOwnerRaw>>(`${ROOT}/owner/${slug}/sign`, payload, ownerHeaders(sessionToken));
    return res.data.data;
}

/** Returns a PDF blob of the compiled but UNSIGNED documents. 400 if forms incomplete. */
export async function getPreview(slug: string, sessionToken: string): Promise<Blob> {
    const res = await publicApi.get<Blob>(`${ROOT}/owner/${slug}/preview`, {
        headers: { 'X-Owner-Session': sessionToken },
        responseType: 'blob',
    });
    return res.data;
}

/** Returns a PDF blob of a single UNSIGNED document preview. Session header required. */
export async function getDocumentPreview(
    slug: string,
    sessionToken: string,
    doc: 'property-setup' | 'owner-form' | 'cohosting',
): Promise<Blob> {
    const res = await publicApi.get<Blob>(`${ROOT}/owner/${slug}/preview/${doc}`, {
        headers: { 'X-Owner-Session': sessionToken },
        responseType: 'blob',
    });
    return res.data;
}

/** OTP mode only: sends a signing OTP to the owner. 429 cooldown; 400 if not OTP mode. */
export async function requestSignOtp(slug: string, sessionToken: string): Promise<RequestSignOtpResult> {
    const res = await publicApi.post<ApiResponse<RequestSignOtpResult>>(
        `${ROOT}/owner/${slug}/sign-otp/request`,
        {},
        ownerHeaders(sessionToken),
    );
    return res.data.data;
}

/** Returns the final PDF URL once available (may 404 until certified -> returns null). */
export async function getFinalDocument(slug: string, sessionToken: string): Promise<string | null> {
    try {
        const res = await publicApi.get<ApiResponse<{ finalPdfUrl?: string } | string>>(
            `${ROOT}/owner/${slug}/document`,
            ownerHeaders(sessionToken),
        );
        const data = res.data.data;
        if (!data) return null;
        if (typeof data === 'string') return data;
        return data.finalPdfUrl ?? null;
    } catch {
        return null;
    }
}

// ------------------------- ADMIN (JWT Bearer) -------------------------

export async function adminCreateOnboarding(payload: CreateOnboardingRequest): Promise<AdminOnboarding> {
    const res = await api.post<ApiResponse<AdminOnboarding>>(`${ROOT}/admin`, payload);
    return res.data.data;
}

export async function adminListOnboardings(): Promise<AdminOnboarding[]> {
    const res = await api.get<ApiResponse<AdminOnboarding[]>>(`${ROOT}/admin`);
    return res.data.data;
}

export async function adminGetOnboarding(id: number): Promise<AdminOnboarding> {
    const res = await api.get<ApiResponse<AdminOnboarding>>(`${ROOT}/admin/${id}`);
    return res.data.data;
}

/** Sensitive data (owner form + property setup) for a single onboarding. */
export async function adminGetOnboardingData(id: number): Promise<OnboardingOwnerData> {
    const res = await api.get<ApiResponse<OnboardingOwnerRaw>>(`${ROOT}/admin/${id}/data`);
    const raw = res.data.data;
    return {
        ...raw,
        propertySetup: safeParse<PropertySetup>(raw.propertySetupData),
        ownerForm: safeParse<OwnerForm>(raw.ownerFormData),
    };
}

export async function adminResendLink(id: number): Promise<void> {
    await api.post<ApiResponse<unknown>>(`${ROOT}/admin/${id}/resend-link`);
}

export async function adminDeleteOnboarding(id: number): Promise<void> {
    await api.delete<ApiResponse<unknown>>(`${ROOT}/admin/${id}`);
}

// --- Note di modifica autorizzate (addendum) ---

/** Aggiunge una nota di modifica a un documento. 409 se l'onboarding è già firmato. */
export async function adminAddAmendment(id: number, payload: AddAmendmentRequest): Promise<AdminOnboarding> {
    const res = await api.post<ApiResponse<AdminOnboarding>>(`${ROOT}/admin/${id}/amendments`, payload);
    return res.data.data;
}

/** Rimuove la nota all'indice indicato per quel documento. 409 se già firmato. */
export async function adminRemoveAmendment(
    id: number,
    document: AmendmentDocKey,
    index: number,
): Promise<AdminOnboarding> {
    const res = await api.delete<ApiResponse<AdminOnboarding>>(`${ROOT}/admin/${id}/amendments/${document}/${index}`);
    return res.data.data;
}

// --- Modifica anagrafica diretta (admin) ---

/** L'admin aggiorna Nome/Cognome/Email/Indirizzo dell'onboarding. 409 se già firmato. */
export async function adminUpdateProfile(id: number, update: AdminProfileUpdate): Promise<AdminOnboarding> {
    const res = await api.put<ApiResponse<AdminOnboarding>>(`${ROOT}/admin/${id}/profile`, update);
    return res.data.data;
}
